import { useState } from 'react'
import { cleanSeatPart, isSeatComplete, type SeatLocation } from '../lib/stadium.js'

/**
 * Modo estadio: ¿dónde estás sentado? Sección / Fila / Asiento (+ nombre
 * opcional). Campos cortos, teclado numérico sugerido pero acepta letras
 * (hay secciones "A", "VIP", "PB"…).
 */
export function StadiumSeatForm({
  initial,
  askName = true,
  submitLabel,
  busy = false,
  onSubmit,
  onCancel,
}: {
  initial?: SeatLocation | null | undefined
  askName?: boolean
  submitLabel: string
  busy?: boolean
  onSubmit: (s: SeatLocation) => void
  onCancel?: () => void
}) {
  const [section, setSection] = useState(initial?.section ?? '')
  const [row, setRow] = useState(initial?.row ?? '')
  const [seat, setSeat] = useState(initial?.seat ?? '')
  const [name, setName] = useState(initial?.name ?? '')
  const [error, setError] = useState<string | null>(null)

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    const value: SeatLocation = {
      section: cleanSeatPart(section),
      row: cleanSeatPart(row),
      seat: cleanSeatPart(seat),
      ...(name.trim() ? { name: name.trim().slice(0, 60) } : {}),
    }
    if (!isSeatComplete(value)) {
      setError('Escribe tu sección, fila y asiento: así te llevamos el pedido.')
      return
    }
    setError(null)
    onSubmit(value)
  }

  return (
    <form className="st-seat" onSubmit={submit} noValidate>
      <div className="st-seat__grid">
        <label className="st-seat__field">
          <span>Sección</span>
          <input
            value={section}
            onChange={(e) => { setSection(e.target.value.toUpperCase()); if (error) setError(null) }}
            placeholder="104"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={12}
            aria-label="Sección"
          />
        </label>
        <label className="st-seat__field">
          <span>Fila</span>
          <input
            value={row}
            onChange={(e) => { setRow(e.target.value.toUpperCase()); if (error) setError(null) }}
            placeholder="12"
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            maxLength={12}
            aria-label="Fila"
          />
        </label>
        <label className="st-seat__field">
          <span>Asiento</span>
          <input
            value={seat}
            onChange={(e) => { setSeat(e.target.value.toUpperCase()); if (error) setError(null) }}
            placeholder="8"
            inputMode="numeric"
            autoComplete="off"
            maxLength={12}
            aria-label="Asiento"
          />
        </label>
      </div>
      {askName && (
        <label className="st-seat__field st-seat__field--wide">
          <span>Tu nombre <em>(opcional)</em></span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Para que te ubiquen al entregar"
            autoComplete="given-name"
            maxLength={60}
            aria-label="Tu nombre"
          />
        </label>
      )}
      {error && <p className="st-error" role="alert">{error}</p>}
      <button type="submit" className="st-btn st-btn--primary" disabled={busy}>
        {busy ? <><span className="spinner" /> Guardando…</> : submitLabel}
      </button>
      {onCancel && (
        <button type="button" className="st-btn st-btn--ghost" onClick={onCancel} disabled={busy}>
          Cancelar
        </button>
      )}
    </form>
  )
}
